import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { IMAGES, STATS } from "@/lib/site";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const LINES = ["Empowering Modern", "Businesses with Smarter", "Collaboration"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden bg-white pt-[70px]">
      <div className="absolute inset-0 lark-grid opacity-70" />
      <motion.div
        style={{ y: yBlob }}
        className="pointer-events-none absolute -top-24 -right-24 h-[520px] w-[520px] rounded-full bg-lark-light blur-3xl opacity-80"
      />
      <motion.div
        style={{ y: yBlob }}
        className="pointer-events-none absolute top-40 -left-40 h-[420px] w-[420px] rounded-full bg-lark-light blur-3xl opacity-60"
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center py-20 lg:py-28">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-lark-blue/20 bg-lark-light px-4 py-1.5 text-xs font-semibold text-lark-blue mb-7"
            data-testid="hero-badge"
          >
            <Sparkles size={14} /> A ByteDance company · Headquartered in Singapore
          </motion.div>

          <h1 className="font-display font-semibold text-5xl md:text-6xl lg:text-[4.4rem] leading-[1.03] tracking-tight text-lark-ink">
            {LINES.map((t, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                >
                  {i === 1 ? (
                    <>
                      Businesses with <span className="text-lark-blue">Smarter</span>
                    </>
                  ) : (
                    t
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-lark-muted"
          >
            Lark Technologies Pte. Ltd. provides modern enterprise software solutions
            designed to help organizations communicate, collaborate, and work more
            efficiently.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#solutions"
              data-testid="hero-learn-more-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-lark-blue px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(22,119,255,0.30)] hover:bg-lark-blueDark hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
            >
              Learn More
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-200" />
            </a>
            <a
              href="#contact"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-2 rounded-full bg-lark-gray px-7 py-3.5 text-sm font-semibold text-lark-ink hover:bg-lark-light transition-colors duration-200"
            >
              Contact Us
            </a>
          </motion.div>
        </div>

        {/* Visual */}
        <motion.div style={{ opacity: fade }} className="relative">
          <motion.div
            style={{ y: yImg }}
            className="relative rounded-[26px] overflow-hidden shadow-[0_30px_80px_rgba(22,119,255,0.18)] border border-white"
          >
            <img
              src={IMAGES.team}
              alt="Enterprise team collaborating"
              className="w-full h-[380px] lg:h-[460px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-lark-blue/25 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="absolute -bottom-7 -left-6 hidden sm:flex items-center gap-4 rounded-2xl bg-white px-5 py-4 shadow-[0_16px_40px_rgba(15,23,42,0.12)] border border-gray-100"
          >
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-lark-light">
              <div className="h-5 w-5 rounded-md bg-lark-blue" />
            </div>
            <div>
              <p className="font-display text-xl font-semibold text-lark-ink leading-none">99.9%</p>
              <p className="text-xs text-lark-muted mt-1">Platform uptime</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Stats strip */}
      <div className="relative border-t border-gray-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="px-6 py-8 first:pl-0"
              data-testid={`hero-stat-${i}`}
            >
              <p className="font-display text-3xl md:text-4xl font-semibold text-lark-ink tracking-tight">{s.value}</p>
              <p className="mt-1.5 text-sm text-lark-muted">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
